import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import FlexBetween from 'components/flexbox/FlexBetween';
import FlexBox from 'components/flexbox/FlexBox';
import SearchInput from 'components/input-fields/SearchInput';
import { H6 } from 'components/Typography';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { searchByName } from 'utils/utils';
import { checkToken } from 'utils/checkToken';
import { employeesApi } from '../../api';
import AllEmployeesDataTable from '../../page-sections/data-table/AllEmployeesDataTable';
import emptyTableImage from 'assets/images/empty-table.png';
import { EmployeeEntity } from 'api/generated';
import toast from 'react-hot-toast';
import { useSeason } from 'contexts/SeasonContext';
import { useEmployeeContext } from 'contexts/EmployeeContext';
import SeasonSwitchModal from 'components/SeasonSwitchModal';

const LoadingSpinner = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <CircularProgress size={60} thickness={4} />
      <Typography variant='h6' color='text.secondary'>
        {t('common.loadingEmployees')}
      </Typography>
    </Box>
  );
};

const AllEmployeesTable: FC = () => {
  const isMobile = useMediaQuery('(max-width:499px)');
  const { t } = useTranslation();
  const { seasonId, setSeasonId } = useSeason();
  const employeeContext = useEmployeeContext();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<EmployeeEntity[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [filteredItem, setFilteredItem] = useState<EmployeeEntity[]>([]);
  const [seasonSwitchModalOpen, setSeasonSwitchModalOpen] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState<EmployeeEntity | null>(null);
  const [targetSeasonName, setTargetSeasonName] = useState('');
  const [targetSeasonId, setTargetSeasonId] = useState<number | null>(null);

  const fetchTableData = () => {
    setIsInitialLoading(true);
    employeesApi
      .getAllEmployees()
      .then(({ data: employees }) => {
        setTableData(employees);
        setIsInitialLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsInitialLoading(false);
        toast.error(t('alerts.errorFetchingEmployees'));
      });
  };

  useEffect(() => {
    fetchTableData();
  }, [seasonId]);

  useEffect(() => {
    if (tableData) {
      const result = searchByName(tableData, searchValue);
      setFilteredItem(result);
    }
  }, [searchValue, tableData]);

  function SelectedRowsMessage() {
    return (
      <H6 mr={2} mt={isMobile ? 1.2 : 0}>
        {selectedRows.length < 2
          ? t('common.tables.select.numSelectedSingular', {
              num: selectedRows.length,
            })
          : t('common.tables.select.numSelected', { num: selectedRows.length })}
      </H6>
    );
  }

  const handleAssignSeason = async (employeeId: number) => {
    try {
      await employeesApi.assignSeason({
        data: {
          employeeId: employeeId.toString(),
          seasonId: seasonId.toString(),
          roomId: '0', // Default or handle room assignment if needed
        },
      });
      toast.success(t('alerts.assignSeasonSuccess'));
      // Optionally refresh data or update local state
      fetchTableData();
    } catch (error) {
      console.error(error);
      toast.error(t('alerts.assignSeasonError'));
    }
  };

  const navigateToEmployee = (employee: EmployeeEntity) => {
    employeeContext.setEmployeeId(employee.id);
    navigate('/dashboard/employees/new', {
      state: { employeeId: employee.id },
    });
  };

  const handleEmployeeClick = (employee: EmployeeEntity) => {
    const isInCurrentSeason = employee.employeesToSeasons?.some(
      (es) => es.seasonId === seasonId,
    );

    if (isInCurrentSeason) {
      navigateToEmployee(employee);
      return;
    }

    // Find the last season by endDate
    const employeeSeasons = employee.employeesToSeasons || [];
    if (employeeSeasons.length === 0) {
      navigateToEmployee(employee);
      return;
    }

    const lastSeason = employeeSeasons
      .filter((es): es is typeof es & { season: NonNullable<typeof es.season> } => !!es.season)
      .sort((a, b) => {
        const dateA = new Date(a.season.endDate).getTime();
        const dateB = new Date(b.season.endDate).getTime();
        return dateB - dateA;
      })[0];

    if (!lastSeason?.season) {
      navigateToEmployee(employee);
      return;
    }

    setPendingEmployee(employee);
    setTargetSeasonId(lastSeason.seasonId);
    setTargetSeasonName(lastSeason.season.name);
    setSeasonSwitchModalOpen(true);
  };

  const handleSeasonSwitchConfirm = async () => {
    if (pendingEmployee && targetSeasonId !== null) {
      try {
        const employeeId = checkToken();
        await employeesApi.setSeasonToDisplay({ employeeId, seasonId: targetSeasonId });
      } catch (error) {
        console.error('Error setting season to display:', error);
      }
      setSeasonId(targetSeasonId);
      setSeasonSwitchModalOpen(false);
      navigateToEmployee(pendingEmployee);
      setPendingEmployee(null);
      setTargetSeasonId(null);
      setTargetSeasonName('');
    }
  };

  const handleSeasonSwitchClose = () => {
    setSeasonSwitchModalOpen(false);
    setPendingEmployee(null);
    setTargetSeasonId(null);
    setTargetSeasonName('');
  };

  return (
    <Box pt={2} pb={4}>
      {isInitialLoading ? (
        <LoadingSpinner />
      ) : tableData.length === 0 ? (
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          minHeight='100vh'
          sx={{ gap: 5 }}
        >
          <img
            src={emptyTableImage}
            alt='Empty table'
            style={{ width: '250px', height: '250px' }}
          />
          <Typography variant='h4' align='center' sx={{ fontWeight: 'bold' }}>
            {t('employees.emptyTable.description')}
          </Typography>
        </Box>
      ) : (
        <>
          <FlexBetween flexWrap='wrap'>
            <FlexBox width={isMobile ? '100%' : 'auto'} alignItems='center'>
              <SearchInput
                bordered={false}
                placeholder={t('employees.searchEmployees')}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
            </FlexBox>
            <FlexBox alignItems='center'>
              {selectedRows.length > 0 && <SelectedRowsMessage />}
            </FlexBox>
          </FlexBetween>

          <AllEmployeesDataTable
            data={filteredItem}
            handleRowSelect={setSelectedRows}
            onAssignSeason={handleAssignSeason}
            onEmployeeClick={handleEmployeeClick}
          />

          <SeasonSwitchModal
            open={seasonSwitchModalOpen}
            onClose={handleSeasonSwitchClose}
            onConfirm={handleSeasonSwitchConfirm}
            seasonName={targetSeasonName}
            employeeName={
              pendingEmployee
                ? `${pendingEmployee.firstName} ${pendingEmployee.lastName}`
                : ''
            }
          />
        </>
      )}
    </Box>
  );
};

export default AllEmployeesTable;
